import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getRestaurantTables, saveRestaurantTable } from "../../components/Api/GmsRestaurantTable";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  getUserPermissions, 
  hasPagePermissionWithAdmin, 
  hasEditPermissionWithAdmin, 
  hasDeletePermissionWithAdmin 
} from "../../components/Permission/PermissionService";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };
  
  let businessId, branchId;
  
  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }
  
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

const defaultForm = {
  tableID: 0,
  tableNo: "",
  tableNameEn: "",
  tableNameAr: "",
  noOfSeats: 0,
  active: true,
  createdBy: 1,
  createdOn: new Date().toISOString(),
  modifiedBy: null,
  modifiedOn: null,
};

const emptyFilter = {
  tableID: null,
  tableNo: "",
  tableNameEn: "",
  tableNameAr: "",
  noOfSeats: null,
  active: null,
};

export default function RestaurantTablePage() {
  const [tables, setTables] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  
  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  
  const session = useMemo(() => getUserSession(), []);

  const formatFullDateTime = (dateTimeStr) => {
    try {
      if (!dateTimeStr) return "N/A";
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadTables();
    }
  }, [permissionsLoading, canView]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);
      
      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsRestaurantTable');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsRestaurantTable');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsRestaurantTable');
      
      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);
      
      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadTables = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getRestaurantTables(filterData);
      setTables(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load tables: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.tableNo || !form.tableNo.trim()) {
      Swal.fire("Error", "Please enter table number", "error");
      return;
    }
    
    if (!form.tableNameEn || !form.tableNameEn.trim()) {
      Swal.fire("Error", "Please enter table name in English", "error");
      return;
    }
    
    if (form.noOfSeats <= 0) {
      Swal.fire("Error", "Please enter a valid number of seats (greater than 0)", "error");
      return;
    }
    
    setLoading(true);
    try {
      const formData = {
        ...form,
        modifiedBy: session.userId,
        modifiedOn: new Date().toISOString(),
      };
      
      if (formData.tableID === 0) {
        formData.createdBy = session.userId;
        formData.createdOn = new Date().toISOString();
      }
      
      const result = await saveRestaurantTable(formData);
      Swal.fire("Success", result.message || "Table saved successfully", "success");
      setForm(defaultForm);
      setIsEditing(false);
      loadTables(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save table: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (table) => {
    setViewLoadingRow(table.tableID);
    try {
      Swal.fire({
        title: table.tableNameEn,
        html: `
          <div class="text-left">
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Table Number:</strong> ${table.tableNo}</p>
              <p><strong>Arabic Name:</strong> ${table.tableNameAr || "-"}</p>
              <p><strong>Number of Seats:</strong> ${table.noOfSeats}</p>
              <p><strong>Status:</strong> ${table.active ? "Active" : "Inactive"}</p>
            </div>

            <div class="mb-3">
              <h6 class="mb-2">Timestamps</h6>
              <p><strong>Created On:</strong> ${formatFullDateTime(table.createdOn)}</p>
              <p><strong>Last Modified:</strong> ${formatFullDateTime(table.modifiedOn)}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load table details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (table) => {
    setForm({
      tableID: table.tableID,
      tableNo: table.tableNo,
      tableNameEn: table.tableNameEn,
      tableNameAr: table.tableNameAr || "",
      noOfSeats: table.noOfSeats,
      active: table.active,
      createdBy: table.createdBy,
      createdOn: table.createdOn,
      modifiedBy: table.modifiedBy,
      modifiedOn: table.modifiedOn,
    });
    setIsEditing(true);
    setActiveTab("1"); // Switch to add/edit tab
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadTables(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadTables(emptyFilter);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      if (tab === "2") {
        loadTables();
      }
    }
  };

  // Get paginated data for display
  const getPaginatedTables = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return tables.slice(startIndex, endIndex);
  };

  // Options for searchable dropdowns
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <ClipLoader size={50} color="#556ee6" />
                  <p className="mt-3">Loading permissions...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {canEdit && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Table Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Table List</span>
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="p-4">
                  {/* Add/Edit Tab */}
                  <TabPane tabId="1">
                    <div>
                      {canEdit && (
                        <Form onSubmit={handleSubmit}>
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <Label for="tableNameEn">Table Name (English) <span className="text-danger">*</span></Label>
                                <Input
                                  type="text"
                                  id="tableNameEn"
                                  name="tableNameEn"
                                  value={form.tableNameEn}
                                  onChange={(e) => handleFormChange("tableNameEn", e.target.value)}
                                  placeholder="Enter table name in English"
                                  required
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label for="tableNameAr">Table Name (Arabic)</Label>
                                <Input
                                  type="text"
                                  id="tableNameAr"
                                  name="tableNameAr"
                                  value={form.tableNameAr}
                                  onChange={(e) => handleFormChange("tableNameAr", e.target.value)}
                                  placeholder="Enter table name in Arabic"
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <Label for="tableNo">Table Number <span className="text-danger">*</span></Label>
                                <Input
                                  type="text"
                                  id="tableNo"
                                  name="tableNo"
                                  value={form.tableNo}
                                  onChange={(e) => handleFormChange("tableNo", e.target.value)}
                                  placeholder="Enter table number"
                                  required
                                />
                              </FormGroup>
                            </Col>
                            <Col md={6}>
                              <FormGroup>
                                <Label for="noOfSeats">Number of Seats <span className="text-danger">*</span></Label>
                                <Input
                                  type="number"
                                  id="noOfSeats"
                                  name="noOfSeats"
                                  value={form.noOfSeats}
                                  onChange={(e) => handleFormChange("noOfSeats", parseInt(e.target.value) || 0)}
                                  min="1"
                                  placeholder="Enter number of seats"
                                  required
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="active"
                                    name="active"
                                    checked={form.active}
                                    onChange={(e) => handleFormChange("active", e.target.checked)}
                                    disabled={!canDelete}
                                  />
                                  <label className="form-check-label" htmlFor="active">
                                    Active
                                  </label>
                                </div>
                              </FormGroup>
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col>
                              <div className="d-flex gap-2">
                                <Button
                                  type="submit"
                                  color="primary"
                                  disabled={loading}
                                  className="waves-effect waves-light"
                                >
                                  {loading && (
                                    <ClipLoader size={16} color="#ffffff" className="me-2" />
                                  )}
                                  {isEditing ? "Update Table" : "Save Table"}
                                </Button>
                                <Button
                                  type="button"
                                  color="secondary"
                                  onClick={handleCancelEdit}
                                  className="waves-effect waves-light"
                                >
                                  Clear
                                </Button>
                              </div>
                            </Col>
                          </Row>
                          </Form>
                        )}
                        {!canEdit && (
                          <p className="text-muted">You don't have permission to add or edit tables.</p>
                        )}
                    </div>
                  </TabPane>

                  {/* List Tab */}
                  <TabPane tabId="2">
                    <div>
                      {/* Filter Section */}
                      <Card className="mb-4">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="card-title mb-0">Filter Tables</h5>
                          <Button
                            color="primary"
                            onClick={() => setShowFilters(!showFilters)}
                          >
                            {showFilters ? "Hide Filters" : "Show Filters"}
                          </Button>
                        </div>
                        {showFilters && (
                          <>
                            <Row>
                              <Col md={3}>
                                <div className="mb-3">
                                  <Label>Table Number</Label>
                                  <Input
                                    value={filter.tableNo}
                                    onChange={(e) => handleFilterChange("tableNo", e.target.value)}
                                    placeholder="Search by table number..."
                                  />
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className="mb-3">
                                  <Label>Table Name (EN)</Label>
                                  <Input
                                    value={filter.tableNameEn}
                                    onChange={(e) => handleFilterChange("tableNameEn", e.target.value)}
                                    placeholder="Search by name..."
                                  />
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className="mb-3">
                                  <Label>Number of Seats</Label>
                                  <Input
                                    type="number"
                                    value={filter.noOfSeats || ""}
                                    onChange={(e) => handleFilterChange("noOfSeats", e.target.value ? parseInt(e.target.value) : null)}
                                    placeholder="Filter by seats..."
                                  />
                                </div>
                              </Col>
                              <Col md={3}>
                                <div className="mb-3">
                                  <Label>Status</Label>
                                  <Input
                                    type="select"
                                    value={filter.active !== null ? (filter.active ? "true" : "false") : ""}
                                    onChange={(e) => handleFilterChange("active", e.target.value === "" ? null : e.target.value === "true")}
                                  >
                                    <option value="">All</option>
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                  </Input>
                                </div>
                              </Col>
                            </Row>
                            <div className="d-flex gap-2">
                              <Button color="primary" onClick={handleApplyFilter}>
                                Apply
                              </Button>
                              <Button color="warning" onClick={handleClearFilter}>
                                Clear
                              </Button>
                            </div>
                          </>
                        )}
                      </CardBody>
                    </Card>

                    {/* Table Section */}
                    <Card>
                      <CardBody>
                        <h5 className="card-title mb-3">
                          Table List ({totalCount} records)
                        </h5>
                        <div className="table-responsive">
                          <Table bordered>
                            <thead className="table-light">
                              <tr>
                                <th>Sr.No</th>
                                <th>Table Number</th>
                                <th>Table Name (EN)</th>
                                <th>Table Name (AR)</th>
                                <th>Number of Seats</th>
                                <th>Active</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr>
                                  <td colSpan="7" className="text-center">
                                    <ClipLoader size={30} color="#556ee6" />
                                  </td>
                                </tr>
                              ) : tables.length === 0 ? (
                                <tr>
                                  <td colSpan="7" className="text-center">
                                    No tables found
                                  </td>
                                </tr>
                              ) : (
                                getPaginatedTables().map((table, index) => (
                                  <tr key={table.tableID}>
                                    <td>{(page - 1) * pageSize + index + 1}</td>
                                    <td>{table.tableNo}</td>
                                    <td>{table.tableNameEn}</td>
                                    <td>{table.tableNameAr || "-"}</td>
                                    <td>{table.noOfSeats}</td>
                                    <td>
                                      <span className={`badge ${table.active ? 'bg-success' : 'bg-danger'}`}>
                                        {table.active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={() => handleView(table)}
                                          disabled={viewLoadingRow === table.tableID}
                                        >
                                          {viewLoadingRow === table.tableID ? (
                                            <ClipLoader size={15} color="white" />
                                          ) : (
                                            <>
                                              <Eye size={16} className="me-1" />
                                              View
                                            </>
                                          )}
                                        </Button>
                                        {canEdit && (
                                          <Button
                                            color="info"
                                            size="sm"
                                            onClick={() => handleEdit(table)}
                                            disabled={editLoadingRow === table.tableID}
                                          >
                                            {editLoadingRow === table.tableID ? (
                                              <ClipLoader size={15} color="white" />
                                            ) : (
                                              <>
                                                <SquarePen size={16} className="me-1" />
                                                Edit
                                              </>
                                            )}
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>

                        {/* Pagination */}
                        <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                             style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                          <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                            <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                            Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                          </div>
                          {totalCount > pageSize && (
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                              <Button
                                color="light"
                                size="sm"
                                disabled={page === 1}
                                onClick={() => handlePageChange(page - 1)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                  color: page === 1 ? '#6c757d' : '#495057'
                                }}
                              >
                                <i className="mdi mdi-chevron-left"></i>
                              </Button>
                              
                              {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                const pageNum = index + 1;
                                const isActive = page === pageNum;
                                
                                return (
                                  <Button
                                    key={pageNum}
                                    color={isActive ? "primary" : "light"}
                                    size="sm"
                                    onClick={() => handlePageChange(pageNum)}
                                    style={{
                                      border: '1px solid #dee2e6',
                                      borderRadius: '0.375rem',
                                      minWidth: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: isActive ? '#556ee6' : '#fff',
                                      borderColor: isActive ? '#556ee6' : '#dee2e6',
                                      color: isActive ? '#fff' : '#495057',
                                      fontWeight: isActive ? '600' : '400',
                                      boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                    }}
                                  >
                                    {pageNum}
                                  </Button>
                                );
                              })}
                              
                              <Button
                                color="light"
                                size="sm"
                                disabled={page === Math.ceil(totalCount / pageSize)}
                                onClick={() => handlePageChange(page + 1)}
                                style={{
                                  border: '1px solid #dee2e6',
                                  borderRadius: '0.375rem',
                                  minWidth: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                  color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                }}
                              >
                                <i className="mdi mdi-chevron-right"></i>
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
